import { ColumnMappingModel } from "services/DataImportService";
import { ResolvedMapping, ResolvedWorksheetMapping } from "./mappingResolution";

type SavedMappingRow = Pick<ColumnMappingModel, "targetColumnName" | "rowIdentifierOrder">;

// Repeats are only left out once the client picks a row identifier. A choice with a column that is
// no longer mapped counts as none, since part of it would treat different rows as repeats.
export function resolveRowIdentifierFields(chosenFields: string[] | undefined, mappedFields: string[]): string[] {
  if (chosenFields?.every((field) => mappedFields.includes(field))) return chosenFields;
  return [];
}

export function keepMappedRowIdentifierFields(
  rowIdentifierPicks: Record<string, string[]>,
  mapping: ResolvedMapping
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(rowIdentifierPicks).filter(
      ([worksheet, fields]) => resolveRowIdentifierFields(fields, listMappedFields(mapping[worksheet] ?? {})).length > 0
    )
  );
}

export function listMappedFields(worksheetMapping: ResolvedWorksheetMapping): string[] {
  return Object.keys(worksheetMapping).filter((field) => Boolean(worksheetMapping[field]));
}

export function areRowIdentifierFieldsEqual(a: Record<string, string[]>, b: Record<string, string[]>): boolean {
  const worksheets = Object.keys(a);
  if (worksheets.length !== Object.keys(b).length) return false;
  return worksheets.every(
    (worksheet) =>
      a[worksheet].length === b[worksheet]?.length &&
      a[worksheet].every((field, index) => field === b[worksheet][index])
  );
}

export function getRowIdentifierOrder(field: string, rowIdentifierFields: string[]): number | undefined {
  const position = rowIdentifierFields.indexOf(field);
  return position >= 0 ? position + 1 : undefined;
}

export function readRowIdentifierFields(rows: SavedMappingRow[]): string[] {
  return rows
    .flatMap((row) =>
      row.targetColumnName && row.rowIdentifierOrder && row.rowIdentifierOrder > 0
        ? [{ field: row.targetColumnName, order: row.rowIdentifierOrder }]
        : []
    )
    .sort((a, b) => a.order - b.order)
    .map((entry) => entry.field);
}

// Worksheets without a saved row identifier are left out.
export function buildRowIdentifierFieldsByWorksheet(
  rows: { worksheet: string; row: SavedMappingRow }[]
): Record<string, string[]> {
  const rowsByWorksheet = new Map<string, SavedMappingRow[]>();
  for (const { worksheet, row } of rows) {
    const worksheetRows = rowsByWorksheet.get(worksheet);
    if (worksheetRows) worksheetRows.push(row);
    else rowsByWorksheet.set(worksheet, [row]);
  }

  const rowIdentifierFields: Record<string, string[]> = {};
  for (const [worksheet, worksheetRows] of rowsByWorksheet) {
    const fields = readRowIdentifierFields(worksheetRows);
    if (fields.length > 0) rowIdentifierFields[worksheet] = fields;
  }
  return rowIdentifierFields;
}
